import {
  ActionIcon,
  Flex,
  Group,
  Indicator,
  MultiSelect,
  Popover,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFilter, IconPlus } from "@tabler/icons-react";
import GroupButton, {
  ActionButton,
} from "components/ui/GroupButton/GroupButton";
import { SearchInput } from "components/ui/SearchInput/SearchInput";
import CreatableSelect from "components/ui/CreatableSelect/CreatableSelect";
import DateRangeInput, {
  DateRangeValue,
} from "components/ui/DateRangeInput/DateRangeInput";
import ExportExcelAction from "components/ui/ExportExcelAction/ExportExcelAction";
import dayjs from "configs/dayjs";
import { SALES_ORDER_STATUS_LABEL } from "constants/common/salesOrder";
import { CommonParamsType } from "constants/types/common";
import {
  SaleOrderParamsType,
  SalesOrderStatus,
} from "constants/types/salesOrder";
import { FC, useMemo } from "react";
import { usePopoverClickOutside } from "utils/hooks/ui/usePopoverClickOutside";
import { useNavigate } from "react-router-dom";
import { SALES_ORDER_CREATE_PAGE } from "routes/route.constant";
import { useCustomers } from "utils/hooks/request/usePartners";
import { useListWarehouse } from "utils/hooks/request/useWarehouses";
import { useUsers } from "utils/hooks";
import { useListProduct } from "utils/hooks/request/useProducts";
import { ExportModule } from "constants/common/exportJob";

type FilterFields = Omit<SaleOrderParamsType, keyof CommonParamsType>;

const STATUS_OPTIONS = Object.entries(SALES_ORDER_STATUS_LABEL).map(
  ([value, label]) => ({ value, label }),
);

const initialParams: CommonParamsType = { page: 1, limit: 100 };

type SalesOrderActionsProps = {
  params: SaleOrderParamsType;
  handleSearch: (v: string) => void;
  onFilterChange: (filters: Partial<FilterFields>) => void;
};

const SalesOrderActions: FC<SalesOrderActionsProps> = ({
  params,
  handleSearch,
  onFilterChange,
}) => {
  const navigate = useNavigate();
  const [filterOpened, { close: closeFilter, toggle: toggleFilter }] =
    useDisclosure(false);
  const { triggerRef, dropdownRef } = usePopoverClickOutside(
    filterOpened,
    closeFilter,
  );

  const { customerOptions, isLoading: customersLoading } = useCustomers();
  const { warehouseOptions, isLoading: warehousesLoading } = useListWarehouse();
  const { userOptions, isLoading: usersLoading } = useUsers(initialParams);
  const { productOptions, isLoading: productsLoading } = useListProduct();

  const dateRangeValue: DateRangeValue = [
    params.fromDate ? dayjs.unix(params.fromDate).toDate() : null,
    params.toDate ? dayjs.unix(params.toDate).toDate() : null,
  ];

  const activeFilterCount = useMemo(
    () =>
      [
        params.partnerId,
        params.warehouseId,
        params.status,
        params.productId,
        params.contactPersonId,
        params.fromDate ?? params.toDate,
      ].filter(Boolean).length,
    [
      params.partnerId,
      params.warehouseId,
      params.status,
      params.productId,
      params.contactPersonId,
      params.fromDate,
      params.toDate,
    ],
  );

  const handleDateRangeChange = (range: DateRangeValue) => {
    const [start, end] = range;
    onFilterChange({
      fromDate: start ? dayjs(start).startOf("day").unix() : undefined,
      toDate: end ? dayjs(end).endOf("day").unix() : undefined,
    });
  };

  const actions: ActionButton[] = [
    {
      key: "create",
      label: "Tạo mới",
      icon: <IconPlus size={18} />,
      variant: "filled",
      color: "blue",
      isLoading: false,
      onClick: () => navigate(SALES_ORDER_CREATE_PAGE),
      permission: "sale_order:create",
    },
  ];

  return (
    <Flex justify="space-between" align="center" gap="sm">
      <Group gap="sm">
        <SearchInput
          value={params.search || ""}
          onChange={handleSearch}
          placeholder="Tìm kiếm đơn bán hàng"
        />
        <Popover
          opened={filterOpened}
          onClose={closeFilter}
          position="bottom-start"
          shadow="md"
          withinPortal
          closeOnClickOutside={false}
        >
          <Popover.Target>
            <Indicator
              label={activeFilterCount}
              size={16}
              disabled={activeFilterCount === 0}
              color="blue"
            >
              <ActionIcon
                ref={triggerRef}
                variant="default"
                size="lg"
                onClick={toggleFilter}
                aria-label="Bộ lọc"
              >
                <IconFilter size={18} />
              </ActionIcon>
            </Indicator>
          </Popover.Target>

          <Popover.Dropdown>
            <div ref={dropdownRef}>
              <Stack gap="sm" w={280}>
                <Text size="sm" fw={600}>
                  Bộ lọc
                </Text>
                <DateRangeInput
                  label="Ngày tạo"
                  value={dateRangeValue}
                  onChange={handleDateRangeChange}
                  size="sm"
                  popoverProps={{ withinPortal: false }}
                />
                <CreatableSelect
                  label="Khách hàng"
                  placeholder="Tất cả"
                  data={customerOptions ?? []}
                  value={params.partnerId ?? null}
                  onChange={(v) =>
                    onFilterChange({ partnerId: v ?? undefined })
                  }
                  allowCreate={false}
                  clearable
                  disabled={customersLoading}
                  size="sm"
                />
                <CreatableSelect
                  label="Kho xuất"
                  placeholder="Tất cả"
                  data={warehouseOptions ?? []}
                  value={params.warehouseId ?? null}
                  onChange={(v) =>
                    onFilterChange({ warehouseId: v ?? undefined })
                  }
                  allowCreate={false}
                  clearable
                  disabled={warehousesLoading}
                  size="sm"
                />
                <MultiSelect
                  label="Trạng thái"
                  placeholder="Tất cả"
                  data={STATUS_OPTIONS}
                  value={params.status ?? []}
                  onChange={(v) =>
                    onFilterChange({
                      status:
                        v.length > 0 ? (v as SalesOrderStatus[]) : undefined,
                    })
                  }
                  clearable
                  size="sm"
                  styles={{
                    pillsList: { flexWrap: "nowrap", overflow: "hidden" },
                  }}
                />
                <CreatableSelect
                  label="Mặt hàng"
                  placeholder="Tất cả"
                  data={productOptions ?? []}
                  value={params.productId ?? null}
                  onChange={(v) =>
                    onFilterChange({ productId: v ?? undefined })
                  }
                  allowCreate={false}
                  clearable
                  disabled={productsLoading}
                  size="sm"
                />
                <CreatableSelect
                  label="Người phụ trách"
                  placeholder="Tất cả"
                  data={userOptions ?? []}
                  value={params.contactPersonId ?? null}
                  onChange={(v) =>
                    onFilterChange({ contactPersonId: v ?? undefined })
                  }
                  allowCreate={false}
                  clearable
                  disabled={usersLoading}
                  size="sm"
                />
              </Stack>
            </div>
          </Popover.Dropdown>
        </Popover>
      </Group>

      <Group gap="sm" wrap="nowrap">
        <ExportExcelAction
          module={ExportModule.SALE_ORDER}
          permission="sale_order:export"
          filters={{
            partnerId: params.partnerId,
            warehouseId: params.warehouseId,
            status: params.status,
            contactPersonId: params.contactPersonId,
          }}
        />
        <GroupButton actions={actions} />
      </Group>
    </Flex>
  );
};

export default SalesOrderActions;
